import type {
  AnalyticsContactChannel,
  AnalyticsEvent,
  AnalyticsProfileField,
} from "@/lib/analytics/analytics-events";
import type { HostProfile, HostProfileDraft } from "./host-profile";
import type { EventDetails } from "./event-details";
import type { Entry, PanelSummary } from "./host-panel-types";

/**
 * The panel's six canonical events, built by pure functions (handoff §15).
 *
 * Separating **building** from emitting is what makes Property 10 verifiable: "no event
 * payload carries personal data" is only testable if the payload exists without a side
 * effect. The rule across all of them is **identifier and count, never content**.
 */

/**
 * Arrival at the credits tab, with the aggregates its header shows, in integer cents.
 *
 * Nothing here emits credit itself: `credit_issued` and `credit_redeemed` are the server's
 * to send (24.7).
 */
export function creditStatementViewed(
  /** Only the three aggregates: the event does not need the rest of the summary. */
  summary: Pick<PanelSummary, "balanceCents" | "inflowCents" | "outflowCents">,
): AnalyticsEvent {
  return {
    event: "credit_statement_viewed",
    // A figure the credit service could not give is left out, not sent as 0: a report must not sum
    // a balance nobody read.
    ...(summary.balanceCents === null ? {} : { balance: summary.balanceCents }),
    entries_total: summary.inflowCents,
    ...(summary.outflowCents === null ? {} : { exits_total: summary.outflowCents }),
  };
}

/** Public link copied. The URL stays out: the fact is "shared", not "to where". */
export function listLinkShared(): AnalyticsEvent {
  return { event: "list_link_shared", surface: "host_panel", method: "clipboard" };
}

/**
 * A gift's detail opened.
 *
 * `has_message` reports that a message exists; neither the message text nor the guest's
 * name leaves this function.
 */
export function giftReceivedOpened(entry: Entry): AnalyticsEvent {
  return {
    event: "gift_received_opened",
    order_id: entry.orderNumber,
    has_message: entry.message !== null && entry.message.trim().length > 0,
    item_count: entry.items.length,
  };
}

/** Support contacted: the channel, and whether the list has an assigned seller. */
export function conciergeContacted(
  channel: AnalyticsContactChannel,
  hasAssignedSeller: boolean,
): AnalyticsEvent {
  return { event: "concierge_contacted", channel, has_assigned_seller: hasAssignedSeller };
}



/**
 * The form's editable fields in reading order, so reports compare fields in one sequence. The
 * e-mail is read-only since handoff v2 §9, so it can never be a changed field.
 */
const PROFILE_FIELDS: readonly (AnalyticsProfileField & keyof HostProfileDraft)[] = ["firstName", "lastName", "phone", "birthDate", "gender"];

/**
 * Which Meus Dados fields changed, **by name** (criterion 24.5).
 *
 * Compares the saved profile against the accepted draft, so resending an identical value is
 * not a change. The values themselves never leave.
 */
export function hostProfileUpdated(before: HostProfile, draft: HostProfileDraft): AnalyticsEvent {
  return {
    event: "host_profile_updated",
    fields_changed: PROFILE_FIELDS.filter((field) => draft[field] !== before[field]),
    validation_failed_fields: [],
  };
}

/**
 * A submit the form refused: which fields, by name, and nothing changed. The names come from the
 * form's own error keys, narrowed to the closed set, so a key the analytics do not know is dropped
 * rather than sent.
 */
export function hostProfileValidationFailed(failedKeys: readonly string[]): AnalyticsEvent {
  return {
    event: "host_profile_updated",
    fields_changed: [],
    validation_failed_fields: PROFILE_FIELDS.filter((field) => failedKeys.includes(field)),
  };
}

/** A piece starred. `limit_reached` says the list now holds the most it may. */
export function itemHighlighted(itemId: string, highlightsCountAfter: number, limit: number): AnalyticsEvent {
  return {
    event: "item_highlighted",
    item_id: itemId,
    highlights_count_after: highlightsCountAfter,
    limit_reached: highlightsCountAfter >= limit,
  };
}

/** What a save of "Dados do evento" changed, by name: the couple, the date, the place and the guest count. */
export function eventDetailsChangedFields(
  before: Pick<EventDetails, "partner1" | "partner2" | "eventDate" | "eventLocation" | "guestCount">,
  patch: {
    partner1: { firstName: string; lastName: string };
    partner2: { firstName: string; lastName: string };
    eventDate: string;
    eventLocation: string;
    guestCount: number | "";
  },
): string[] {
  const changed: string[] = [];
  const differs = (a: { firstName: string; lastName: string }, b: { firstName: string; lastName: string }) =>
    a.firstName !== b.firstName || a.lastName !== b.lastName;

  if (differs(patch.partner1, before.partner1)) changed.push("partner1");
  if (differs(patch.partner2, before.partner2)) changed.push("partner2");
  if (patch.eventDate !== before.eventDate) changed.push("eventDate");
  if (patch.eventLocation.trim() !== before.eventLocation.trim()) changed.push("eventLocation");
  if ((patch.guestCount === "" ? null : patch.guestCount) !== before.guestCount) changed.push("guestCount");

  return changed;
}

/*
 * ─── V2 Events (Dados do Evento & Gestão) ───────────────────────────────────
 */

export function eventDetailsUpdated(fieldsChanged: readonly string[]): AnalyticsEvent {
  return { event: "event_details_updated", fields_changed: fieldsChanged };
}

export function welcomeMessageUpdated(messageLength: number): AnalyticsEvent {
  return { event: "welcome_message_updated", message_length: messageLength };
}

export function listCoverUpdated(isBrandPlaceholder: boolean): AnalyticsEvent {
  return { event: "list_cover_updated", is_brand_placeholder: isBrandPlaceholder };
}

export function supportModeEntered(role: string, origin: "list_report" | "list_search"): AnalyticsEvent {
  return { event: "support_mode_entered", role, origin };
}

export function supportModeExited(destination: string): AnalyticsEvent {
  return { event: "support_mode_exited", destination };
}

export function listAdminChanged(field: "admin" | "advisory", action: "set" | "remove"): AnalyticsEvent {
  return { event: "list_admin_changed", field, action };
}

export function catalogSearchSubmitted(context: string, resultsCount: number): AnalyticsEvent {
  return { event: "catalog_search_submitted", context, results_count: resultsCount };
}
